import React from 'react';
import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { ATTACHMENT_CONFIG_ATTRIBUTES, extraClaimsInfo, specialDocumentTypes } from '../../services/constants';
import AttachmentMetaNumber from './AttachmentMetaNumber';
import AttachmentMetaText from './AttachmentMetaText';
import { CombinedFile } from './combined/CombinedFile';
import { useAttachmentsConfig } from '../../hooks/useAttachmentsConfig';
import {
  getAttachmentConfig,
  getAttachmentConfigValue,
  getCombinedAttachmentTypes,
  getUnsupportedAttachmentTypes,
} from '../../services/attachments.helper';
import { useAttachmentsValidations } from '../../hooks/useAttachmentsValidations';
import { useAttachmentMetaFields } from './useAttachmentMetaFields';
import { CustomSelectBox } from '../../../../common/CustomSelectBox';

/**
 * Nasty non-configurable wrapper for the meta fields that should be refactored to use a configurable component.
 *
 * @param attachmentType attachment root type
 * @param attachmentSubType attachment subtype
 * @param classes style classes
 * @param readOnly readOnly mode
 * @param metaForm the form that manages the meta fields
 * @param allAttachmentsMetadata form attachments data
 * @param parentClasses form root classes
 * @param attachmentsUiSchema uiSchema of current attachments field
 * @param jsonSchema global jsonSchema
 * @returns {JSX.Element}
 * @constructor
 */
const AttachmentMetaFields = ({
  attachmentType,
  attachmentSubType,
  classes,
  readOnly,
  metaForm,
  allAttachmentsMetadata,
  parentClasses,
  attachmentsUiSchema,
  jsonSchema,
}) => {
  const { t } = useTranslation();
  const { attachmentTypesConfig } = useAttachmentsConfig();
  const attachmentConfig = getAttachmentConfig(attachmentTypesConfig, attachmentType, attachmentSubType);

  const hideMetaFields = getAttachmentConfigValue(attachmentConfig, ATTACHMENT_CONFIG_ATTRIBUTES.HIDE_META_FIELDS);
  const metaFieldRequired = getAttachmentConfigValue(
    attachmentConfig,
    ATTACHMENT_CONFIG_ATTRIBUTES.META_FIELD_REQUIRED,
  );
  const extraClaims = _.get(attachmentConfig, extraClaimsInfo.EXTRAS);

  const {
    isAttachmentWithClaims,
    isAttachmentWithDrawings,
    isAttachmentWithAbstract,
    extractDropdownDefinition,
  } = useAttachmentMetaFields();

  const {
    optionalNumberValidation,
    requiredNumberValidation,
    documentTypeValidation,
    figureToBePublishedValidation,
    requiredValidation,
  } = useAttachmentsValidations();

  const getClaimsMetaFields = () => {
    const metaFieldNeeded = getAttachmentConfigValue(
      attachmentConfig,
      ATTACHMENT_CONFIG_ATTRIBUTES.NEED_NUMBER_OF_CLAIMS,
    );

    const claimsValidation =
      metaFieldRequired === true ? requiredNumberValidation('claims.number') : optionalNumberValidation();

    if (!isAttachmentWithClaims(attachmentSubType) || !metaFieldNeeded) return null;
    return (
      <Box className={classes.boxIndent}>
        <AttachmentMetaNumber
          id="numberOfClaims"
          label={`${t('claims.number')}`}
          form={metaForm}
          required={metaFieldRequired}
          readOnly={readOnly}
          className={classes.technicalField}
          validations={claimsValidation}
        />
      </Box>
    );
  };

  const getAdditionalClaimsMetaFields = () => {
    if (!extraClaims) return null;
    return (
      <Box className={classes.column}>
        {extraClaims?.map((claim) => {
          return (
            <Box key={claim.id} className={classes.boxIndent}>
              <AttachmentMetaNumber
                id={claim?.id}
                label={`${t(claim?.label)}`}
                form={metaForm}
                required={metaFieldRequired}
                readOnly={readOnly}
                className={classes.saTechnicalField}
                validations={requiredNumberValidation(claim?.label)}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  const getDrawingsMetaFields = () => {
    const numberOfDrawingsValidation = metaFieldRequired
      ? requiredNumberValidation('drawings.number')
      : optionalNumberValidation();

    if (!isAttachmentWithDrawings(attachmentSubType)) return null;

    return (
      <Box className={classes.boxIndent}>
        <AttachmentMetaNumber
          id="numberOfDrawings"
          label={`${t('drawings.number')}`}
          form={metaForm}
          required={metaFieldRequired}
          readOnly={readOnly}
          className={classes.technicalField}
          validations={numberOfDrawingsValidation}
        />
      </Box>
    );
  };

  const getAbstractMetaFields = () => {
    if (!isAttachmentWithAbstract(attachmentSubType)) return null;

    return (
      <Box className={classes.boxIndent}>
        <AttachmentMetaText
          id="figureToBePublished"
          label={t('attachments.abstract.figure.published')}
          form={metaForm}
          readOnly={readOnly}
          className={classes.technicalField}
          InputProps={{ inputProps: { maxLength: 4 } }}
          validations={figureToBePublishedValidation}
        />
      </Box>
    );
  };

  const getOtherMetaFields = () => {
    if (attachmentSubType !== specialDocumentTypes.OTHER) return null;
    return (
      <Box className={classes.boxIndent}>
        <AttachmentMetaText
          id="documentType"
          label={t('document.type.specify')}
          form={metaForm}
          readOnly={readOnly}
          className={classes.additionalField}
          rows={3}
          InputProps={{ inputProps: { maxLength: 128 } }}
          validations={documentTypeValidation('document.type.specify')}
        />
      </Box>
    );
  };

  const getCombinedMetaFields = () => {
    if (attachmentSubType !== specialDocumentTypes.COMBINED) {
      return null;
    }

    const includedAttachmentTypes = getCombinedAttachmentTypes(
      attachmentTypesConfig,
      getUnsupportedAttachmentTypes(attachmentsUiSchema),
    );

    return (
      <Box className={classes.boxIndent}>
        <CombinedFile
          className={classes.boxIndent}
          attachmentsMetadata={allAttachmentsMetadata}
          form={metaForm}
          parentClasses={parentClasses}
          readOnly={readOnly}
          attachmentTypesConfig={attachmentTypesConfig}
          includedAttachmentTypes={includedAttachmentTypes}
        />
      </Box>
    );
  };

  const getDropdownMetaField = () => {
    const dropdownEnumDefinition = extractDropdownDefinition(attachmentConfig, jsonSchema);

    if (!dropdownEnumDefinition) {
      return null;
    }

    const maxOneAttachmentPerType = getAttachmentConfigValue(
      attachmentConfig,
      ATTACHMENT_CONFIG_ATTRIBUTES.MAX_ONE_ATTACHMENT_PER_SUBTYPE,
    );
    const excludedDropdownValues = maxOneAttachmentPerType
      ? allAttachmentsMetadata
        .filter(
          attachment =>
            attachment.attachmentSubType === attachmentType && attachment.attachmentType === attachmentSubType,
        )
        .map(attachment => attachment[dropdownEnumDefinition.name])
      : [];

    const dropdownValidation =
      dropdownEnumDefinition.required === true ? requiredValidation(dropdownEnumDefinition.name) : null;

    const dropdownElements = dropdownEnumDefinition?.enum.map((currElement, index) => {
      return {
        itemId: currElement,
        itemLabel: dropdownEnumDefinition.enumNames[index],
      };
    });

    return (
      <Box className={classes.boxIndent}>
        <CustomSelectBox
          id={`${attachmentType}-additional-dropdown`}
          className={classes.technicalField}
          name={dropdownEnumDefinition.name}
          form={metaForm}
          itemId="itemId"
          itemLabel="itemLabel"
          items={dropdownElements}
          label={dropdownEnumDefinition.title}
          defaultValue=""
          disabledItems={excludedDropdownValues}
          validationRules={dropdownValidation}
        />
      </Box>
    );
  };

  return (
    <>
      {!hideMetaFields &&
        (getClaimsMetaFields() ??
          getDrawingsMetaFields() ??
          getAdditionalClaimsMetaFields() ??
          getAbstractMetaFields() ??
          getOtherMetaFields() ??
          getCombinedMetaFields())}
      {!hideMetaFields && getDropdownMetaField()}
    </>
  );
};

export default AttachmentMetaFields;
